import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import {
  palette,
  radius,
  spacing,
  typography,
} from '../../style-utils/theme';
import { AppButton } from '../button/app-button';
import {
  LocationFailure,
  LocationPermission,
  declinedRecently,
  locationPermission,
  rememberDeclined,
  requestDeviceLocation,
  turnLocationBackOnHelp,
} from '../../function-utils/device-location';

/**
 * THE APP'S OWN ASK FOR THE DEVICE'S LOCATION (R-T10, Plan C WP-C11).
 *
 * One sentence saying what the location is for, "Use my location" and "Not
 * now". The BROWSER's prompt appears only after the tap — a prompt nobody asked
 * for is refused, and Safari then stays refused.
 *
 *   permission      what is drawn
 *   ─────────────   ─────────────────────────────────────────────────────────
 *   granted         nothing. `onLocated` is called with the position (when
 *                   `useWhenGranted`), exactly as if the person had tapped.
 *   prompt/unknown  the ask — unless "Not now" was answered on this device in
 *                   the last 30 days.
 *   denied          a sentence saying so, and how to turn it back on, in words
 *                   for the device in front of the person.
 *   unsupported     nothing.
 *
 * Used in exactly two places: the building forms (no pin yet — centre the map)
 * and signup (offer the city the person is in). The position never sets
 * anything by itself; the caller only centres or offers.
 */

export interface DevicePosition {
  latitude: number;
  longitude: number;
}

export type LocationAskState = 'checking' | 'showing' | 'hidden';

export interface LocationAskProps {
  /** What the location is for, in one sentence. */
  purpose: string;
  /** The position, after a tap (or straight away when already granted).
   *  `asked` is true when the person TAPPED for it — a caller may then act on
   *  it more firmly than on a position it was merely allowed to use. */
  onLocated: (position: DevicePosition, how: { asked: boolean }) => void;
  /** Told where the ask stands — 'checking' until the permission is known,
   *  then 'showing' or 'hidden' — so a host with a "Use my location" control of
   *  its own shows it ONLY once the ask is known to be out of the way. A plain
   *  yes/no was not enough: while the permission is still being read the answer
   *  is neither, and a host that guessed drew two buttons for a frame. */
  onState?: (state: LocationAskState) => void;
  /** Use an already-granted permission without showing anything. */
  useWhenGranted?: boolean;
  /** Stand down entirely (the form is read-only, the map is not up yet). */
  disabled?: boolean;
  className?: string;
}

const Box = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  padding: ${spacing.sm};
  margin: ${spacing.xs} 0;
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  background: ${palette.tealTintSoft};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.inkSoft};
  overflow-wrap: anywhere;
`;

const Line = styled.p`
  margin: 0;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.xs};
`;

type Phase =
  | 'checking'
  | 'hidden'
  | 'ask'
  | 'asking'
  | 'denied'
  | 'unavailable';

const phaseFor = (permission: LocationPermission): Phase => {
  if (permission === 'denied') return 'denied';
  if (permission === 'prompt' || permission === 'unknown') {
    return declinedRecently() ? 'hidden' : 'ask';
  }

  // granted, unsupported
  return 'hidden';
};

export const LocationAsk: React.FC<LocationAskProps> = ({
  purpose,
  onLocated,
  useWhenGranted = true,
  disabled = false,
  className,
  onState,
}) => {
  const [phase, setPhase] = useState<Phase>('checking');
  const showing = !disabled && phase !== 'checking' && phase !== 'hidden';
  const state: LocationAskState = disabled
    ? 'hidden'
    : phase === 'checking'
      ? 'checking'
      : showing
        ? 'showing'
        : 'hidden';
  const tellState = useRef(onState);
  tellState.current = onState;

  useEffect(() => {
    tellState.current?.(state);
  }, [state]);
  const alive = useRef(true);
  // The newest callback, so an effect that ran once still reaches it.
  const located = useRef(onLocated);
  located.current = onLocated;

  const locate = useCallback(async (silent: boolean): Promise<void> => {
    if (!silent) setPhase('asking');
    const outcome = await requestDeviceLocation();
    if (!alive.current) return;
    if (outcome.ok) {
      setPhase('hidden');
      located.current(
        { latitude: outcome.latitude, longitude: outcome.longitude },
        { asked: !silent }
      );

      return;
    }
    // Used silently (already granted) a failure is not worth a sentence: the
    // screen works without a location, and the person asked for nothing.
    if (silent) return;
    const reason: LocationFailure = outcome.reason;
    setPhase(reason === 'denied' ? 'denied' : reason === 'unavailable' ? 'unavailable' : 'hidden');
  }, []);

  useEffect(() => {
    alive.current = true;
    if (disabled) {
      setPhase('hidden');

      return () => {
        alive.current = false;
      };
    }

    locationPermission()
      .then(permission => {
        if (!alive.current) return;
        setPhase(phaseFor(permission));
        if (permission === 'granted' && useWhenGranted) void locate(true);
      })
      .catch(() => {
        if (alive.current) setPhase('hidden');
      });

    return () => {
      alive.current = false;
    };
  }, [disabled, useWhenGranted, locate]);

  if (!showing) return null;

  if (phase === 'denied') {
    return (
      <Box className={className} role="status" data-location-ask="denied">
        <Line>
          Location is switched off for Lobbero on this device, so we cannot find where
          you are. Everything here still works without it.
        </Line>
        <Line>{turnLocationBackOnHelp()}</Line>
      </Box>
    );
  }

  if (phase === 'unavailable') {
    return (
      <Box className={className} role="status" data-location-ask="unavailable">
        <Line>
          Your location could not be found just now. Everything here still works without
          it.
        </Line>
        <Actions>
          <AppButton type="button" variant="secondary" onClick={() => void locate(false)}>
            Try again
          </AppButton>
        </Actions>
      </Box>
    );
  }

  return (
    <Box className={className} data-location-ask="ask">
      <Line>{purpose}</Line>
      <Actions>
        <AppButton
          type="button"
          variant="secondary"
          loading={phase === 'asking'}
          loadingLabel="Finding your location"
          onClick={() => void locate(false)}
        >
          Use my location
        </AppButton>
        <AppButton
          type="button"
          variant="ghost"
          disabled={phase === 'asking'}
          onClick={() => {
            rememberDeclined();
            setPhase('hidden');
          }}
        >
          Not now
        </AppButton>
      </Actions>
    </Box>
  );
};
